/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { FaBook, FaPlus, FaTrash, FaEdit, FaTimes, FaCalendarAlt, FaInfoCircle, FaLanguage, FaTimesCircle, FaTrophy, FaGraduationCap, FaBriefcase, FaChevronDown, FaHistory, FaCheckCircle, FaClock } from 'react-icons/fa';
import * as authService from '../services/authService';
import * as masterService from '../services/masterService';
import * as ebService from '../services/ebService';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-[200] px-6 py-3 rounded-lg shadow-lg ${type === 'success' ? 'bg-green-500' : 'bg-red-500'
      } text-white font-semibold`}>
      {message}
    </div>
  );
};

const EBManageTitles = () => {
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchSubject, setSearchSubject] = useState('');
  const [searchStream, setSearchStream] = useState('');
  const [searchSpecialization, setSearchSpecialization] = useState('');
  const [searchLanguage, setSearchLanguage] = useState('');

  // Dropdown visibility states
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Refs for dropdown containers
  const subjectRef = useRef(null);
  const streamRef = useRef(null);
  const specializationRef = useRef(null);
  const languageRef = useRef(null);

  // Master Data
  const [subjects, setSubjects] = useState([]);
  const [streams, setStreams] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [articleTitles, setArticleTitles] = useState([]);

  useEffect(() => {
    const init = async () => {
      const data = await loadMasterData();
      await loadMyTitles(data);
    };
    init();

    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      console.warn('User not authenticated');
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectRef.current && !subjectRef.current.contains(event.target)) setShowSubjectDropdown(false);
      if (streamRef.current && !streamRef.current.contains(event.target)) setShowStreamDropdown(false);
      if (specializationRef.current && !specializationRef.current.contains(event.target)) setShowSpecializationDropdown(false);
      if (languageRef.current && !languageRef.current.contains(event.target)) setShowLanguageDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMasterData = async () => {
    try {
      const [subjectsData, streamsData, specializationsData, languagesData] = await Promise.all([
        masterService.getAllSubjects(),
        masterService.getAllStreams(),
        masterService.getAllSpecializations(),
        masterService.getAllLanguages()
      ]);

      const s = subjectsData.success ? subjectsData.data : (Array.isArray(subjectsData) ? subjectsData : []);
      const m = streamsData.success ? streamsData.data : (Array.isArray(streamsData) ? streamsData : []);
      const sp = specializationsData.success ? specializationsData.data : (Array.isArray(specializationsData) ? specializationsData : []);
      const l = languagesData.success ? languagesData.data : (Array.isArray(languagesData) ? languagesData : []);

      setSubjects(s);
      setStreams(m);
      setSpecializations(sp);
      setLanguages(l);

      return { subjects: s, streams: m, specializations: sp, languages: l };
    } catch (error) {
      console.error('Error loading master data:', error);
      showToast('Failed to load master data', 'error');
      return { subjects: [], streams: [], specializations: [], languages: [] };
    }
  };

  const loadMyTitles = async (masterData = null) => {
    try {
      const response = await ebService.getMyRunningTitles();
      let titles = response.success ? response.data : [];

      const currentLanguages = masterData ? masterData.languages : languages;
      const currentSubjects = masterData ? masterData.subjects : subjects;
      const currentStreams = masterData ? masterData.streams : streams;
      const currentSpecs = masterData ? masterData.specializations : specializations;

      const mappedTitles = titles.map(title => {
        // Resolve Names (Same logic as admin page)
        let subjectName = title.subject?.name || (currentSubjects.find(s => s.id === title.subject_id)?.name) || 'N/A';
        let streamName = title.stream?.name || (currentStreams.find(s => s.id === title.stream_id)?.name) || '';
        let specName = title.specialization?.name || (currentSpecs.find(s => s.id === title.specialization_id)?.name) || '';

        return {
          ...title,
          subjectName,
          streamName,
          specName,
          languages: Array.isArray(title.languages_allowed) ? title.languages_allowed.map(code => {
            const lang = currentLanguages.find(l => l.code === code || l.id === code);
            return lang ? lang.name : code;
          }) : []
        };
      });

      setArticleTitles(mappedTitles);
    } catch (error) {
      console.error('Error loading my titles:', error);
      showToast('Failed to load your titles', 'error');
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    stream: '',
    specialization: '',
    languages: [],
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectLanguage = (lang) => {
    if (!formData.languages.includes(lang.name)) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, lang.name]
      }));
      setSearchLanguage('');
      setShowLanguageDropdown(false);
    }
  };

  const handleRemoveLanguage = (langName) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.filter(l => l !== langName)
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) return showToast('Please enter title', 'error'), false;
    if (!formData.subject) return showToast('Please select department', 'error'), false;
    if (!formData.stream) return showToast('Please select stream', 'error'), false;
    if (formData.languages.length === 0) return showToast('Please add at least one language', 'error'), false;
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const subjectId = subjects.find(s => s.name === formData.subject)?.id;
      const streamId = streams.find(s => s.name === formData.stream)?.id;
      const specId = specializations.find(s => s.name === formData.specialization)?.id;
      const languageCodes = formData.languages.map(name => {
        const lang = languages.find(l => l.name === name);
        return lang ? (lang.code || lang.id) : null;
      }).filter(c => c);

      const payload = {
        title: formData.title,
        subject_id: subjectId,
        stream_id: streamId,
        specialization_id: specId,
        language_ids: languageCodes,
      };

      await ebService.createRunningTitle(payload);
      showToast('Title submitted for admin approval!', 'success');
      setShowModal(false);
      await loadMyTitles();
    } catch (error) {
      showToast(error.message || 'Failed to submit title', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING_APPROVAL':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold flex items-center gap-1"><FaClock className="w-3 h-3" /> Pending Approval</span>;
      case 'ACTIVE':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1"><FaCheckCircle className="w-3 h-3" /> Active</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold">{status}</span>;
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-300 text-base";
  const labelClass = "block text-base font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-3">
                <FaTrophy className="text-white text-3xl" />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Manage Article Titles</h1>
                <p className="text-gray-500 mt-1 flex items-center gap-2">
                  <FaInfoCircle className="text-indigo-500" />
                  Create titles to be verified and published by the admin
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFormData({ title: '', subject: '', stream: '', specialization: '', languages: [] });
                setShowModal(true);
              }}
              className="group flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-4 rounded-2xl font-bold shadow-lg hover:shadow-2xl hover:scale-[1.02] active:scale-95 transition-all duration-300"
            >
              <FaPlus className="group-hover:rotate-90 transition-transform duration-300" />
              Create New Article Title
            </button>
          </div>
        </div>

        {/* Titles Grid/Table */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 mb-10">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Title Info</th>
                  <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Department & Stream</th>
                  <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Languages</th>
                  <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Dates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {articleTitles.length > 0 ? (
                  articleTitles.map((rt) => (
                    <tr key={rt.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-6 py-6">
                        <div className="max-w-xs">
                          <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{rt.title}</p>
                          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5 font-mono text-[10px]">
                            <FaInfoCircle className="text-gray-300" />
                            ID: {rt.id.slice(0, 8)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-sm">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            <span className="font-medium text-gray-700">{rt.subjectName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            <span className="text-gray-500">{rt.streamName}</span>
                          </div>
                          {rt.specName && (
                            <div className="flex items-center gap-2 text-[10px] pl-4">
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400"></span>
                              <span className="text-gray-400 italic">{rt.specName}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 text-sm">
                        <div className="flex flex-wrap gap-1.5">
                          {rt.languages.map((l, i) => (
                            <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-[10px] uppercase font-bold border border-indigo-100">{l}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        {getStatusBadge(rt.status)}
                      </td>
                      <td className="px-6 py-6 text-sm">
                        <div className="space-y-1">
                          <p className="text-gray-500">Deadline: <span className="font-medium text-gray-900">{rt.submission_deadline ? new Date(rt.submission_deadline).toLocaleDateString() : 'TBD'}</span></p>
                          <p className="text-gray-500">Results: <span className="font-medium text-gray-900">{rt.result_date ? new Date(rt.result_date).toLocaleDateString() : 'TBD'}</span></p>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center">
                        <FaHistory className="text-gray-200 text-6xl mb-4" />
                        <p className="text-gray-500 font-medium">No titles created yet.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-100">
            {articleTitles.length > 0 ? (
              articleTitles.map((rt) => (
                <div key={rt.id} className="p-6 space-y-4 active:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-bold text-gray-900 leading-tight">{rt.title}</h3>
                    {getStatusBadge(rt.status)}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Department</p>
                      <p className="text-sm font-semibold text-gray-700">{rt.subjectName}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Stream</p>
                      <p className="text-sm text-gray-600">{rt.streamName}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {rt.languages.map((l, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-[10px] uppercase font-bold border border-indigo-100">{l}</span>
                    ))}
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3 flex justify-between items-center text-[11px]">
                    <div className="text-gray-500">
                      <span className="block opacity-60">Deadline</span>
                      <span className="font-bold text-gray-900">{rt.submission_deadline ? new Date(rt.submission_deadline).toLocaleDateString() : 'TBD'}</span>
                    </div>
                    <div className="text-right text-gray-500">
                      <span className="block opacity-60">Results</span>
                      <span className="font-bold text-gray-900">{rt.result_date ? new Date(rt.result_date).toLocaleDateString() : 'TBD'}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-20 text-center">
                <FaHistory className="text-gray-200 text-6xl mb-4 mx-auto" />
                <p className="text-gray-500 font-medium">No titles created yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Draft Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 sm:p-6 lg:p-8 animate-fade-in">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-all animate-modal-slide-up border border-white/20">
            <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 p-6 md:p-10 relative overflow-hidden">
              {/* Decorative Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/20 rounded-full -ml-12 -mb-12 blur-xl"></div>

              <div className="relative z-10 text-white flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div className="pr-12 md:pr-0">
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-tight">Create New Article Title
                  </h2>
                  <p className="text-indigo-100 text-xs md:text-sm mt-1 font-medium opacity-90">Create a global topic for Young India students</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-0 right-0 md:relative bg-white/10 hover:bg-white/20 p-2.5 md:p-3 rounded-xl md:rounded-2xl transition-all backdrop-blur-md border border-white/10 shadow-xl"
                >
                  <FaTimes className="text-lg md:text-xl" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 scrollbar-hide">
              <div className="space-y-6">
                <div className="group">
                  <label className={labelClass}>Article Title *</label>
                  <div className="relative">
                    <input
                      type="text" name="title" value={formData.title} onChange={handleFormChange}
                      className={`${inputClass} !bg-gray-50 group-hover:!bg-white group-focus-within:!bg-white`}
                      placeholder="e.g. The Future of Sustainable Mobility"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div ref={subjectRef} className="group">
                    <label className={labelClass}>Department *</label>
                    <div className="relative">
                      <input
                        type="text" value={searchSubject || formData.subject}
                        onChange={e => setSearchSubject(e.target.value)}
                        onFocus={() => setShowSubjectDropdown(true)}
                        placeholder="Search Dept..."
                        className={`${inputClass} !bg-gray-50 group-hover:!bg-white group-focus-within:!bg-white`}
                      />
                      {showSubjectDropdown && (
                        <div className="absolute w-full mt-2 bg-white border-2 border-indigo-50 rounded-2xl shadow-2xl z-[150] max-h-56 overflow-y-auto p-2 space-y-1 animate-dropdown-in">
                          {subjects.filter(s => s.name.toLowerCase().includes(searchSubject.toLowerCase())).map(s => (
                            <button
                              key={s.id}
                              onClick={() => {
                                setFormData({ ...formData, subject: s.name, stream: '', specialization: '' });
                                setSearchSubject('');
                                setShowSubjectDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-indigo-50 rounded-xl transition-all font-semibold text-gray-700 hover:text-indigo-700 flex items-center gap-3"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-200"></span> {s.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div ref={streamRef} className="group">
                    <label className={labelClass}>Stream *</label>
                    <div className="relative">
                      <input
                        type="text" value={searchStream || formData.stream}
                        onChange={e => setSearchStream(e.target.value)}
                        onFocus={() => setShowStreamDropdown(true)}
                        placeholder={formData.subject ? "Search Stream..." : "Select Dept First"}
                        disabled={!formData.subject}
                        className={`${inputClass} !bg-gray-50 group-hover:!bg-white group-focus-within:!bg-white disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                      {showStreamDropdown && (
                        <div className="absolute w-full mt-2 bg-white border-2 border-indigo-50 rounded-2xl shadow-2xl z-[150] max-h-56 overflow-y-auto p-2 space-y-1 animate-dropdown-in">
                          {streams
                            .filter(s => {
                              const selectedSub = subjects.find(sub => sub.name === formData.subject);
                              return !selectedSub || s.subject_id === selectedSub.id;
                            })
                            .filter(s => s.name.toLowerCase().includes(searchStream.toLowerCase()))
                            .map(s => (
                              <button
                                key={s.id}
                                onClick={() => {
                                  setFormData({ ...formData, stream: s.name, specialization: '' });
                                  setSearchStream('');
                                  setShowStreamDropdown(false);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-indigo-50 rounded-xl transition-all font-semibold text-gray-700 hover:text-indigo-700 flex items-center gap-3"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-200"></span> {s.name}
                              </button>
                            ))}
                          {streams.filter(s => {
                            const selectedSub = subjects.find(sub => sub.name === formData.subject);
                            return !selectedSub || s.subject_id === selectedSub.id;
                          }).length === 0 && (
                              <div className="p-4 text-center text-gray-400 text-sm">No streams found for this department</div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div ref={languageRef} className="group">
                  <label className={labelClass}>Languages *</label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formData.languages.map(l => (
                      <span key={l} className="bg-indigo-600 text-white px-4 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg shadow-indigo-100 transition-all hover:scale-105">
                        {l} <FaTimesCircle className="cursor-pointer text-indigo-200 hover:text-white transition-colors" onClick={() => handleRemoveLanguage(l)} />
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      type="text" value={searchLanguage} onChange={e => setSearchLanguage(e.target.value)}
                      onFocus={() => setShowLanguageDropdown(true)}
                      placeholder="Add language..."
                      className={`${inputClass} !bg-gray-50 group-hover:!bg-white group-focus-within:!bg-white`}
                    />
                    {showLanguageDropdown && (
                      <div className="absolute w-64 mt-2 bg-white border-2 border-indigo-50 rounded-2xl shadow-2xl z-[150] max-h-48 overflow-y-auto p-2 space-y-1 animate-dropdown-in">
                        {languages.filter(l => l.name.toLowerCase().includes(searchLanguage.toLowerCase())).map(l => (
                          <button
                            key={l.id}
                            onClick={() => handleSelectLanguage(l)}
                            className="w-full px-4 py-3 text-left hover:bg-indigo-50 rounded-xl transition-all font-semibold text-gray-700 hover:text-indigo-700"
                          >
                            {l.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div ref={specializationRef} className="group">
                  <label className={labelClass}>Specialization (Optional)</label>
                  <div className="relative">
                    <input
                      type="text" value={searchSpecialization || formData.specialization}
                      onChange={e => setSearchSpecialization(e.target.value)}
                      onFocus={() => setShowSpecializationDropdown(true)}
                      placeholder={formData.stream ? "Search Specialization..." : "Select Stream First"}
                      disabled={!formData.stream}
                      className={`${inputClass} !bg-gray-50 group-hover:!bg-white group-focus-within:!bg-white disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                    {showSpecializationDropdown && (
                      <div className="absolute w-full mt-2 bg-white border-2 border-indigo-50 rounded-2xl shadow-2xl z-[150] max-h-56 overflow-y-auto p-2 space-y-1 animate-dropdown-in">
                        {specializations
                          .filter(s => {
                            const selectedStream = streams.find(str => str.name === formData.stream);
                            return !selectedStream || s.stream_id === selectedStream.id;
                          })
                          .filter(s => s.name.toLowerCase().includes(searchSpecialization.toLowerCase()))
                          .map(s => (
                            <button
                              key={s.id}
                              onClick={() => {
                                setFormData({ ...formData, specialization: s.name });
                                setSearchSpecialization('');
                                setShowSpecializationDropdown(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-indigo-50 rounded-xl transition-all font-semibold text-gray-700 hover:text-indigo-700"
                            >
                              {s.name}
                            </button>
                          ))}
                        {specializations.filter(s => {
                          const selectedStream = streams.find(str => str.name === formData.stream);
                          return !selectedStream || s.stream_id === selectedStream.id;
                        }).length === 0 && (
                            <div className="p-4 text-center text-gray-400 text-sm">No specializations found for this stream</div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary Section */}
                <div className="bg-indigo-50/70 rounded-[2rem] border-2 border-indigo-100/50 p-6 md:p-8 space-y-5 mt-10 relative overflow-hidden backdrop-blur-sm">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-full -mr-12 -mt-12"></div>
                  <h3 className="text-xl font-black text-indigo-900 flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-100">
                      <FaInfoCircle className="text-white text-sm" />
                    </div>
                    Quick Summary
                  </h3>
                  <div className="grid grid-cols-1 gap-4 text-sm font-medium">
                    {[
                      { label: 'Topic Name', value: formData.title, highlight: true },
                      { label: 'Dept.', value: formData.subject },
                      { label: 'Stream', value: formData.stream },
                      { label: 'Special.', value: formData.specialization || 'Not specified' },
                      { label: 'Languages', value: formData.languages.join(', ') }
                    ].map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white/40 p-3.5 rounded-2xl border border-white/60">
                        <span className="text-indigo-400 font-bold uppercase tracking-tighter text-[10px]">{item.label}</span>
                        <span className={`text-right ${item.value ? 'text-indigo-900 font-bold' : 'text-gray-300 italic'} ${item.highlight ? 'text-base line-clamp-2' : ''}`}>
                          {item.value || 'Required'}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center bg-gradient-to-r from-indigo-600 to-indigo-700 p-4 rounded-2xl shadow-xl shadow-indigo-100 mt-2">
                      <span className="text-white/70 font-black uppercase tracking-widest text-[10px]">Publishing Price</span>
                      <span className="font-black text-white text-lg">AUTO-MANAGED</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8 bg-gray-50 border-t-2 border-gray-100 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="order-2 sm:order-1 flex-1 py-4 bg-white border-2 border-indigo-100 text-indigo-500 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 hover:text-indigo-600 transition-all active:scale-95 shadow-sm"
              >
                Cancel Draft
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="order-1 sm:order-2 flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-70 flex items-center justify-center gap-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="text-lg" />
                    Submit for Admin Approval
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EBManageTitles;
